import { useState, type ChangeEvent } from 'react';
import { apiErrorText, useImportCeo1CStatementMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { decodeStatement } from './statementFile';
import {
  ErrorNotice,
  FormActions,
  FormBox,
  FormHint,
  FormTitle,
  Input,
  SuccessNotice,
  UploadWarning,
} from './styles';

interface UploadedFile {
  name: string;
  ok: boolean;
  text: string;
  warnings: string[];
}

interface StatementUploadFormProps {
  onClose: () => void;
}

/** Загрузка выписок файлами: путь для банков без API и запасной путь для всех остальных. */
export function StatementUploadForm({ onClose }: StatementUploadFormProps) {
  const [importStatement] = useImportCeo1CStatementMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploaded, setUploaded] = useState<UploadedFile[]>([]);

  async function handleFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setIsUploading(true);
    const done: UploadedFile[] = [];
    // По одному файлу: у каждого свой итог, и ошибка в одном не теряет остальные.
    for (const file of files) {
      try {
        const content = decodeStatement(await file.arrayBuffer());
        const res = await importStatement({ content }).unwrap();
        done.push({
          name: file.name,
          ok: true,
          text: `счетов: ${res.synced_accounts}, операций: ${res.ingested_payments}`,
          warnings: res.warnings,
        });
      } catch (err: unknown) {
        done.push({
          name: file.name,
          ok: false,
          text: apiErrorText(err, 'Файл не похож на выписку 1С'),
          warnings: [],
        });
      }
      setUploaded([...done]);
    }
    setIsUploading(false);
  }

  return (
    <FormBox>
      <FormTitle>Загрузить выписку из интернет-банка</FormTitle>
      <FormHint>
        В интернет-банке выгрузите выписку для 1С: файл .txt в формате 1CClientBankExchange. Счета,
        остатки и операции появятся сами. Можно выбрать несколько файлов сразу. Повторная загрузка
        того же периода не задваивает операции.
      </FormHint>
      <Input
        type="file"
        accept=".txt,text/plain"
        multiple
        disabled={isUploading}
        onChange={handleFiles}
      />
      {uploaded.map((file) => (
        <div key={file.name}>
          {file.ok ? (
            <SuccessNotice>
              {file.name}: {file.text}
            </SuccessNotice>
          ) : (
            <ErrorNotice>
              {file.name}: {file.text}
            </ErrorNotice>
          )}
          {file.warnings.map((warning) => (
            <UploadWarning key={warning}>{warning}</UploadWarning>
          ))}
        </div>
      ))}
      <FormActions>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose}>
          {uploaded.length > 0 ? 'Готово' : 'Отмена'}
        </Button>
      </FormActions>
    </FormBox>
  );
}
